import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { LoadingState } from '../common/LoadingState';
import { supabase } from '../../lib/supabaseClient';
import { getSuperAdminAccess } from '../../services/superAdminService';
import { Button } from '../common/Button';

interface SuperAdminProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

type AccessState = 'loading' | 'authorized' | 'unauthenticated' | 'forbidden' | 'timeout';

const ADMIN_ACCESS_TIMEOUT_MS = 5000;

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error('Admin access check timed out'));
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

export function SuperAdminProtectedRoute({
  children,
  redirectTo = '/',
}: SuperAdminProtectedRouteProps) {
  const location = useLocation();
  const [accessState, setAccessState] = useState<AccessState>('loading');

  useEffect(() => {
    let isMounted = true;

    const checkAccess = async () => {
      try {
        setAccessState('loading');

        const {
          data: { session },
        } = await withTimeout(supabase.auth.getSession(), ADMIN_ACCESS_TIMEOUT_MS);

        if (!session) {
          if (isMounted) {
            setAccessState('unauthenticated');
          }
          return;
        }

        await withTimeout(getSuperAdminAccess(), ADMIN_ACCESS_TIMEOUT_MS);

        if (isMounted) {
          setAccessState('authorized');
        }
      } catch (error) {
        if (isMounted) {
          const message = error instanceof Error ? error.message : '';
          setAccessState(message === 'Admin access check timed out' ? 'timeout' : 'forbidden');
        }
      }
    };

    void checkAccess();

    return () => {
      isMounted = false;
    };
  }, [location.pathname]);

  if (accessState === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-honey-bg-soft px-4">
        <LoadingState spinnerSize="xl" className="py-0" />
      </div>
    );
  }

  if (accessState === 'unauthenticated') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (accessState !== 'authorized') {
    const isTimeout = accessState === 'timeout';

    return (
      <div className="flex min-h-screen items-center justify-center bg-honey-bg-soft px-4">
        <div className="w-full max-w-md rounded-2xl border border-amber-200 bg-white p-8 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">Internal</div>
          <h1 className="mt-3 text-2xl font-semibold text-[#2F2F2F]">
            {isTimeout ? 'Access check timed out' : 'Access denied'}
          </h1>
          <p className="mt-3 text-sm leading-6 text-gray-600">
            {isTimeout
              ? 'Honey could not verify admin access in time, so the panel was blocked by default. Please try again or sign out.'
              : 'This area is reserved for Honey internal super admins. Your current account is authenticated, but it does not have permission to open the super admin panel.'}
          </p>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            {isTimeout ? (
              <Button type="button" variant="brand" onClick={() => window.location.reload()}>
                Retry
              </Button>
            ) : (
              <Button type="button" variant="brand" onClick={() => window.location.assign(redirectTo)}>
                Back to dashboard
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                await supabase.auth.signOut();
                window.location.assign('/login');
              }}
            >
              Sign out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}